import { SlotProxy, slotProxy } from '@oracle/oraclejet-webdriver';
import { By, error } from 'selenium-webdriver';
import { MessageToastWebElementBase } from './MessageToastWebElementBase';

/**
 * The component WebElement for [oj-c-message-toast](../../jsdocs/oj-c.MessageToast.html).
 * Do not instantiate this class directly, instead, use
 * [findMessageToast](../functions/findMessageToast.html).
 */
export class MessageToastWebElement extends MessageToastWebElementBase {
  /**
   * Retrieve a SlotProxy which represents the custom detail content of a single message.
   * @param messageLocator.key The key within the MessageToast's dataset associated with the individual message.
   * @throws {Error} when the detail content rendered is not provided by the application
   * @returns The detail content of the message rendered by the application
   * @since "15.0.4"
   */
  async findDetail<K>(messageLocator: { key: K }): Promise<SlotProxy> {
    const key = messageLocator.key;
    const layerId = await this.getAttribute('data-oj-messages-layer-id');
    const messagesContainer = await this.getMessagesContainer(layerId);
    const messageSelector = this.getMessageSelector(key);
    const customDetailContentSelector = '[data-oj-message-custom-detail]';

    try {
      const el = await messagesContainer.findElement(
        By.css(`${messageSelector} ${customDetailContentSelector}`)
      );
      return slotProxy(el, this);
    } catch (e) {
      throw new error.NoSuchElementError(
        'No corresponding detail template or renderer found for the messageLocator.'
      );
    }
  }

  /**
   * Retrieve a SlotProxy which represents the custom icon content of a single message.
   * @param messageLocator.key The key within the MessageToast's dataset associated with the individual message.
   * @throws {Error} when the icon content rendered is not provided by the application
   * @returns The icon content of the message rendered by the application
   * @since "15.0.4"
   */
  async findIcon<K>(messageLocator: { key: K }): Promise<SlotProxy> {
    const key = messageLocator.key;
    const layerId = await this.getAttribute('data-oj-messages-layer-id');
    const messagesContainer = await this.getMessagesContainer(layerId);
    const messageSelector = this.getMessageSelector(key);
    const customIconContentSelector = '[data-oj-message-custom-icon]';

    try {
      const el = await messagesContainer.findElement(
        By.css(`${messageSelector} ${customIconContentSelector}`)
      );
      return slotProxy(el, this);
    } catch (e) {
      throw new error.NoSuchElementError(
        'No corresponding icon template or renderer found for the messageLocator.'
      );
    }
  }

  /**
   * Closes the message identified by the provided key.
   * @param messageLocator.key The key within the MessageToast's dataset associated with the individual message.
   * @throws {Error} when the specified is not closable (closeAffordance set to "off")
   * @since "15.0.4"
   */
  async doClose<K>(messageLocator: { key: K }): Promise<void> {
    const key = messageLocator.key;
    const layerId = await this.getAttribute('data-oj-messages-layer-id');
    const messagesContainer = await this.getMessagesContainer(layerId);
    const messageSelector = this.getMessageSelector(key);
    const closeButtonSelector = '[data-oj-message-close-button] button';

    try {
      const el = await messagesContainer.findElement(
        By.css(`${messageSelector} ${closeButtonSelector}`)
      );
      await el.click();
    } catch (e) {
      throw new error.UnsupportedOperationError(
        'The close operation is not applicable for the messageLocator.'
      );
    }
  }

  /**
   * Finds the layered content of this messages instance.
   *
   * @param id The layer id for this instance of the message
   * @returns The layer content
   */
  private getMessagesContainer(id: string) {
    const driver = this.getDriver();
    return driver.findElement(By.id(id));
  }

  /**
   * Creates the CSS selector for the message with the provided key.
   *
   * @param key The key of the message
   * @returns the css selector that can used to fetch the message
   */
  private getMessageSelector<K>(key: K) {
    return typeof key === 'number'
      ? `[data-oj-key="number-${key}"]`
      : `[data-oj-key="string-${key}"]`;
  }
}
